/**
 * Admin · AI models & routing (/admin/ai-models, migration 0092).
 *
 * Two things, one screen:
 *   ACTIVITIES — for each AI activity the product performs, an ordered model chain (primary, then
 *                fallbacks) and ONE switch. Off = the code default, exactly as before this screen
 *                existed. On = the chain, within ~30 s on every instance, no deploy.
 *   PRICE BOOK — every model / metered service and its rate. ai_call_log and the Spend rollup both
 *                price from here; a price change bumps the row's version so history is never re-priced.
 *
 * Beside each activity: what it ACTUALLY ran on in the last 30 days (ai_call_log), so the screen shows
 * reality, not only intent. Owner-only to change; everyone on the platform surface can look.
 */
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Alert, Info, Plus, X } from "../../components/icons.js";
import { Modal, Switch, apiErrorText } from "./adminUi.js";
import { EvaluationModeCard } from "./EvaluationModeCard.js";

interface AiModel {
  key: string;
  provider: "gemini" | "anthropic" | "azure_speech";
  model: string;
  displayName: string;
  enabled: boolean;
  pricingUnit: "token" | "audio_second" | "character" | "request";
  inputUsdPerMTok: number | null;
  outputUsdPerMTok: number | null;
  listPriceUsd: number | null;
  listPricePer: number | null;
  listPriceLabel: string | null;
  priceSource: string | null;
  priceVerifiedOn: string | null;
  priceVersion: number;
  notes: string | null;
  routable: boolean;
}
interface Route {
  modelKey: string;
  priority: number;
  enabled: boolean;
}
interface Activity {
  key: string;
  label: string;
  description: string;
  group: string;
  runsIn: "api" | "worker";
  providers: string[];
  codeDefault: string;
  useConfiguredRoutes: boolean;
  routes: Route[];
  effective: { source: string; chain: { provider: string; model: string; routeRank: number | null }[] };
  usage30d: { provider: string; model: string; calls: number; failed: number; routed: number; costInr: number | null; lastAt: string }[];
}
interface Config {
  models: AiModel[];
  activities: Activity[];
  cacheTtlSeconds: number;
}

const QK = ["admin-ai-models"];
const PROVIDER_LABEL: Record<string, string> = { gemini: "Gemini", anthropic: "Claude", azure_speech: "Azure Speech" };
const inr = (n: number | null): string => (n == null ? "—" : `₹${n < 1 ? n.toFixed(4) : n.toFixed(2)}`);

export function AiModels(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const { data, isLoading, error } = useQuery({ queryKey: QK, queryFn: () => api.get<Config>("/admin/ai-models") });
  const isForbidden = error instanceof ApiError && error.status === 403;

  const groups = useMemo(() => {
    const out = new Map<string, Activity[]>();
    for (const a of data?.activities ?? []) out.set(a.group, [...(out.get(a.group) ?? []), a]);
    return [...out.entries()];
  }, [data]);

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>AI models &amp; routing</h1>
          <div className="sub">Which model each AI activity runs on, with fallbacks · the central price book · owner-only to change · audited</div>
        </div>
        <span className="pill n">Owner</span>
      </div>

      {!isOwner && (
        <div className="banner info" style={{ marginBottom: 16 }}>
          <Info />
          <div>You can view the configuration, but <b>only the owner can change it</b>.</div>
        </div>
      )}
      {isLoading && <div className="card pad mut sm">Loading AI model configuration…</div>}
      {error && (
        <div className={isForbidden ? "banner info" : "banner crit"}>
          <span>{isForbidden ? "Your admin role doesn't have access to AI model configuration." : apiErrorText(error, "Couldn't load AI model configuration.")}</span>
        </div>
      )}

      {data && (
        <>
          <EvaluationModeCard isOwner={isOwner} />
          <div className="banner warn" style={{ marginBottom: 16 }}>
            <Alert />
            <div>
              An activity runs its <b>code default</b> until you switch <b>Use configured models</b> on. When on, the
              primary is tried first and each fallback in order if a call fails. Changes reach every server within
              ~{data.cacheTtlSeconds} s. An activity only offers the providers its code can actually call.
            </div>
          </div>

          {groups.map(([group, acts]) => (
            <div key={group} className="card" style={{ marginBottom: 16 }}>
              <div className="card-h">
                <h3>{group}</h3>
                <span className="tag">{acts.length} activit{acts.length === 1 ? "y" : "ies"}</span>
              </div>
              <div className="card-b">
                {acts.map((a) => (
                  <ActivityRow key={a.key} activity={a} models={data.models} isOwner={isOwner} />
                ))}
              </div>
            </div>
          ))}

          <PriceBook models={data.models} isOwner={isOwner} />
        </>
      )}

      <div className="disclaimer">
        <b>Measurement, not billing.</b> Rates here price TalentHop&apos;s own AI spend (ai_call_log and the Spend
        screen). Customer credit prices are separate and unaffected. Every change is written to the audit log.
      </div>
    </section>
  );
}

// ── one activity ───────────────────────────────────────────────────────────

function ActivityRow({ activity: a, models, isOwner }: { activity: Activity; models: AiModel[]; isOwner: boolean }): ReactNode {
  const qc = useQueryClient();
  const [on, setOn] = useState(a.useConfiguredRoutes);
  const [routes, setRoutes] = useState<Route[]>(a.routes);
  const [adding, setAdding] = useState("");
  useEffect(() => {
    setOn(a.useConfiguredRoutes);
    setRoutes(a.routes);
  }, [a.useConfiguredRoutes, a.routes]);

  const byKey = useMemo(() => new Map(models.map((m) => [m.key, m])), [models]);
  const candidates = models.filter((m) => m.routable && a.providers.includes(m.provider) && !routes.some((r) => r.modelKey === m.key));
  const dirty = on !== a.useConfiguredRoutes || JSON.stringify(routes.map((r) => [r.modelKey, r.enabled])) !== JSON.stringify(a.routes.map((r) => [r.modelKey, r.enabled]));

  const save = useMutation({
    mutationFn: () =>
      api.put(`/admin/ai-models/activities/${encodeURIComponent(a.key)}`, {
        useConfiguredRoutes: on,
        routes: routes.map((r) => ({ modelKey: r.modelKey, enabled: r.enabled })),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QK }),
  });

  const move = (i: number, d: -1 | 1): void =>
    setRoutes((rs) => {
      const j = i + d;
      if (j < 0 || j >= rs.length) return rs;
      const next = [...rs];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });

  return (
    <div style={{ padding: "14px 0", borderBottom: "1px solid var(--line-soft)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600 }}>{a.label}</span>
            <code className="mut sm">{a.key}</code>
            <span className={a.useConfiguredRoutes ? "pill t" : "pill n"}>{a.useConfiguredRoutes ? "Configured models" : "Code default"}</span>
            {a.effective.source === "configured_unusable" && <span className="pill r">No usable model — code default</span>}
            <span className="pill n" style={{ fontSize: 12 }}>{a.runsIn === "api" ? "API" : "Worker"}</span>
          </div>
          <div className="mut sm" style={{ marginTop: 3 }}>{a.description}</div>
          <div className="mut sm" style={{ marginTop: 3 }}>
            Code default: <code>{a.codeDefault}</code> · Providers this code can call: {a.providers.map((p) => PROVIDER_LABEL[p] ?? p).join(", ")}
          </div>
          <div className="mut sm" style={{ marginTop: 3 }}>
            Last 30 days:{" "}
            {a.usage30d.length === 0
              ? "no calls recorded"
              : a.usage30d
                  .map((u) => `${u.model} — ${u.calls} call${u.calls === 1 ? "" : "s"}${u.failed ? ` (${u.failed} failed)` : ""}, ${inr(u.costInr)}${u.routed ? `, ${u.routed} via routing` : ""}`)
                  .join(" · ")}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
          <span className="sm mut">Use configured models</span>
          <Switch on={on} disabled={!isOwner || save.isPending} onToggle={() => setOn((v) => !v)} />
        </div>
      </div>

      <div className="tbl-wrap" style={{ marginTop: 10 }}>
        <table className="tbl">
          <thead>
            <tr><th style={{ width: 90 }}>Slot</th><th>Model</th><th>Provider</th><th>Price</th><th>Active</th><th /></tr>
          </thead>
          <tbody>
            {routes.length === 0 && (
              <tr><td colSpan={6} className="mut sm">No models in the chain — the code default runs.</td></tr>
            )}
            {routes.map((r, i) => {
              const m = byKey.get(r.modelKey);
              return (
                <tr key={r.modelKey}>
                  <td><span className={i === 0 ? "pill t" : "pill n"}>{i === 0 ? "Primary" : `Fallback ${i}`}</span></td>
                  <td>
                    {m?.displayName ?? r.modelKey}
                    <div className="mut sm"><code>{m?.model ?? r.modelKey}</code>{m && !m.enabled && <> · <b>model disabled — skipped</b></>}</div>
                  </td>
                  <td>{PROVIDER_LABEL[m?.provider ?? ""] ?? m?.provider}</td>
                  <td className="tnum sm">{m ? priceText(m) : "—"}</td>
                  <td>
                    <Switch
                      on={r.enabled}
                      disabled={!isOwner}
                      onToggle={() => setRoutes((rs) => rs.map((x, k) => (k === i ? { ...x, enabled: !x.enabled } : x)))}
                    />
                  </td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    <button className="btn sm ghost" disabled={!isOwner || i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-tip="Move up">↑</button>
                    <button className="btn sm ghost" disabled={!isOwner || i === routes.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-tip="Move down">↓</button>
                    <button className="btn sm ghost" disabled={!isOwner} onClick={() => setRoutes((rs) => rs.filter((_, k) => k !== i))} aria-label="Remove" data-tip="Remove">
                      <X />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
        <select className="input" style={{ maxWidth: 320 }} value={adding} disabled={!isOwner || candidates.length === 0} onChange={(e) => setAdding(e.target.value)}>
          <option value="">{candidates.length === 0 ? "No other compatible models" : "Add a fallback model…"}</option>
          {candidates.map((m) => (
            <option key={m.key} value={m.key}>
              {m.displayName} ({PROVIDER_LABEL[m.provider]}){m.enabled ? "" : " — disabled"}
            </option>
          ))}
        </select>
        <button
          className="btn sm"
          disabled={!isOwner || !adding}
          onClick={() => {
            setRoutes((rs) => [...rs, { modelKey: adding, priority: rs.length + 1, enabled: true }]);
            setAdding("");
          }}
        >
          <Plus /> Add
        </button>
        <span style={{ flex: 1 }} />
        {save.isError && <span className="sm" style={{ color: "var(--crit)" }}>{apiErrorText(save.error, "Couldn't save.")}</span>}
        {save.isSuccess && !dirty && <span className="sm mut">Saved · audited</span>}
        <button className="btn sm primary" disabled={!isOwner || !dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function priceText(m: AiModel): string {
  if (m.pricingUnit === "token") return `$${m.inputUsdPerMTok} in / $${m.outputUsdPerMTok} out per 1M tokens`;
  return `$${m.listPriceUsd} per ${m.listPriceLabel ?? `${m.listPricePer} ${m.pricingUnit}`}`;
}

// ── price book ────────────────────────────────────────────────────────────

/** The whole price book as a Markdown table — pastes cleanly into a chat assistant for comparison. */
function priceBookMarkdown(models: AiModel[]): string {
  const cell = (v: unknown): string => (v == null || v === "" ? "—" : String(v).replace(/\|/g, "\\|"));
  const rows = models.map((m) =>
    [m.displayName, m.model, PROVIDER_LABEL[m.provider] ?? m.provider, m.pricingUnit, priceText(m), m.enabled ? "yes" : "no", m.routable ? "yes" : "price only", m.priceSource, m.priceVerifiedOn, `v${m.priceVersion}`, m.notes]
      .map(cell)
      .join(" | "),
  );
  return [
    `TalentHop AI price book (USD list prices), copied ${new Date().toISOString().slice(0, 10)}`,
    "",
    "| Model | Model id | Provider | Unit | Price | Enabled | Routable | Source | Checked | Version | Notes |",
    "|---|---|---|---|---|---|---|---|---|---|---|",
    ...rows.map((r) => `| ${r} |`),
  ].join("\n");
}

function PriceBook({ models, isOwner }: { models: AiModel[]; isOwner: boolean }): ReactNode {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AiModel | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<"" | "ok" | "fail">("");
  const copyAll = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(priceBookMarkdown(models));
      setCopied("ok");
    } catch {
      setCopied("fail");
    }
    setTimeout(() => setCopied(""), 2000);
  };
  const toggle = useMutation({
    mutationFn: (m: AiModel) => api.put(`/admin/ai-models/models/${encodeURIComponent(m.key)}`, { enabled: !m.enabled }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QK }),
  });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <h3>Price book</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="tag">{models.length} models &amp; services</span>
          <button className="btn sm" onClick={() => void copyAll()} title="Copy every model and its rates as a Markdown table, e.g. to compare in an AI chat">
            {copied === "ok" ? "Copied" : copied === "fail" ? "Copy failed" : "Copy all"}
          </button>
          <button className="btn sm" disabled={!isOwner} onClick={() => setCreating(true)}><Plus /> Add model</button>
        </div>
      </div>
      <div className="card-b">
        <div className="mut sm" style={{ marginBottom: 10 }}>
          The single source of AI rates. A disabled model is skipped in every configured chain (an activity on its
          code default is unaffected). Changing a price bumps its version — past calls keep the rate they were priced at.
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Model</th><th>Provider</th><th>Price</th><th>Source</th><th>Version</th><th>Enabled</th><th /></tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.key}>
                  <td>{m.displayName}<div className="mut sm"><code>{m.model}</code>{!m.routable && " · price only"}</div></td>
                  <td>{PROVIDER_LABEL[m.provider] ?? m.provider}</td>
                  <td className="tnum sm">{priceText(m)}</td>
                  <td className="sm mut">{m.priceSource ?? "—"}{m.priceVerifiedOn ? ` · checked ${m.priceVerifiedOn}` : ""}</td>
                  <td className="tnum">v{m.priceVersion}</td>
                  <td><Switch on={m.enabled} disabled={!isOwner || toggle.isPending || !m.routable} onToggle={() => toggle.mutate(m)} /></td>
                  <td><button className="btn sm ghost" disabled={!isOwner} onClick={() => setEditing(m)}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {toggle.isError && <div className="banner crit" style={{ marginTop: 10 }}><span>{apiErrorText(toggle.error, "Couldn't update the model.")}</span></div>}
      </div>
      {editing && <ModelEditor model={editing} onClose={() => setEditing(null)} />}
      {creating && <ModelEditor model={null} onClose={() => setCreating(false)} />}
    </div>
  );
}

function ModelEditor({ model, onClose }: { model: AiModel | null; onClose: () => void }): ReactNode {
  const qc = useQueryClient();
  const creating = model === null;
  const [f, setF] = useState({
    provider: model?.provider ?? "gemini",
    model: model?.model ?? "",
    displayName: model?.displayName ?? "",
    pricingUnit: model?.pricingUnit ?? "token",
    inputUsdPerMTok: String(model?.inputUsdPerMTok ?? ""),
    outputUsdPerMTok: String(model?.outputUsdPerMTok ?? ""),
    listPriceUsd: String(model?.listPriceUsd ?? ""),
    listPricePer: String(model?.listPricePer ?? ""),
    listPriceLabel: model?.listPriceLabel ?? "",
    priceSource: model?.priceSource ?? "",
    priceVerifiedOn: model?.priceVerifiedOn ?? "",
    notes: model?.notes ?? "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((cur) => ({ ...cur, [k]: e.target.value }));
  const token = f.pricingUnit === "token";
  const n = (v: string): number | undefined => (v.trim() === "" ? undefined : Number(v));
  const opt = (v: string): string | null => (v.trim() === "" ? null : v.trim());

  const save = useMutation({
    mutationFn: () => {
      const prices = token
        ? { inputUsdPerMTok: n(f.inputUsdPerMTok), outputUsdPerMTok: n(f.outputUsdPerMTok) }
        : { listPriceUsd: n(f.listPriceUsd), listPricePer: n(f.listPricePer), listPriceLabel: opt(f.listPriceLabel) };
      const common = { displayName: f.displayName.trim(), priceSource: opt(f.priceSource), priceVerifiedOn: opt(f.priceVerifiedOn), notes: opt(f.notes), ...prices };
      return creating
        ? api.post("/admin/ai-models/models", { provider: f.provider, model: f.model.trim(), pricingUnit: f.pricingUnit, ...common })
        : api.put(`/admin/ai-models/models/${encodeURIComponent(model.key)}`, common);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
      onClose();
    },
  });

  const field = (label: string, input: ReactNode) => (
    <div className="field" style={{ marginBottom: 10 }}>
      <label>{label}</label>
      {input}
    </div>
  );

  return (
    <Modal
      title={creating ? "Add model" : `Edit ${model.displayName}`}
      onClose={onClose}
      maxWidth={520}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
          {save.isError && <span className="sm" style={{ color: "var(--crit)", flex: 1 }}>{apiErrorText(save.error, "Couldn't save.")}</span>}
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={save.isPending || !f.displayName.trim() || (creating && !f.model.trim())} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      {creating && (
        <>
          {field(
            "Provider",
            <select
              className="input"
              value={f.provider}
              onChange={(e) => setF((cur) => ({ ...cur, provider: e.target.value as AiModel["provider"], pricingUnit: e.target.value === "azure_speech" ? "audio_second" : "token" }))}
            >
              <option value="gemini">Gemini</option>
              <option value="anthropic">Claude (Anthropic)</option>
              <option value="azure_speech">Azure Speech (metered, price only)</option>
            </select>,
          )}
          {field("Model id (exactly as the provider's API names it)", <input className="input" value={f.model} onChange={set("model")} placeholder="e.g. gemini-2.5-flash" />)}
          {f.provider === "azure_speech" &&
            field(
              "Billing unit",
              <select className="input" value={f.pricingUnit} onChange={set("pricingUnit")}>
                <option value="audio_second">Audio seconds</option>
                <option value="character">Characters</option>
                <option value="request">Requests</option>
              </select>,
            )}
        </>
      )}
      {field("Display name", <input className="input" value={f.displayName} onChange={set("displayName")} />)}
      {token ? (
        <div style={{ display: "flex", gap: 10 }}>
          {field("Input USD / 1M tokens", <input className="input tnum" type="number" min={0} step="0.001" value={f.inputUsdPerMTok} onChange={set("inputUsdPerMTok")} />)}
          {field("Output USD / 1M tokens", <input className="input tnum" type="number" min={0} step="0.001" value={f.outputUsdPerMTok} onChange={set("outputUsdPerMTok")} />)}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10 }}>
          {field("List price USD", <input className="input tnum" type="number" min={0} step="0.0001" value={f.listPriceUsd} onChange={set("listPriceUsd")} />)}
          {field(`Covers how many ${f.pricingUnit.replace("_", " ")}s`, <input className="input tnum" type="number" min={1} value={f.listPricePer} onChange={set("listPricePer")} />)}
          {field("Label", <input className="input" value={f.listPriceLabel} onChange={set("listPriceLabel")} placeholder="1 Hour" />)}
        </div>
      )}
      {field("Price source", <input className="input" value={f.priceSource} onChange={set("priceSource")} placeholder="Where this rate comes from" />)}
      {field("Price checked on", <input className="input" type="date" value={f.priceVerifiedOn} onChange={set("priceVerifiedOn")} />)}
      {field("Notes", <input className="input" value={f.notes} onChange={set("notes")} />)}
      {!creating && (
        <div className="mut sm">Changing a price creates version v{(model?.priceVersion ?? 1) + 1}; calls already recorded keep their original rate.</div>
      )}
    </Modal>
  );
}
